/* eslint-disable array-bracket-newline */
/**
 * Row with ticker
 */
import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { ITicker, IWatchedTicker, enums } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Image from '../../atoms/Image';
import ColorPicker from './ColorPicker';
import useTickerLivePrice from './hooks/useTickerLivePrice';
import useTicker from '../../hooks/useTicker';
import PriceAlertCreate from './PriceAlertCreate';
import DailyPriceChange from './DailyPriceChange';
import notice from '../../assets/images/notice.svg';
import flash from '../../assets/images/flash.svg';
import { createGetAlerts } from '../../selectors/priceAlerts';
import { decorateExchangeName } from '../../helpers/decorators';
import NumberAnimation from '../../molecules/NumberAnimation';
import UnfavouriteStar from './UnfavouriteStar';
import { capitalize } from '../Modal/TickersModal/helpers';
import { UpdateWatchlistItemPayload } from '../../reducers/watchlist';

const getAlertProps = (
  isFirst: boolean,
  hasAlerts: boolean,
  account: boolean
) => {
  const props: Record<string, string> = {};
  const classes = ['watchlist__row__alert', 'icon-alarm-add-alt'];

  if (isFirst) {
    props['data-tip'] = 'Set price<br />alert for this<br />pair';
    props['data-for'] = 'watchlist-first-item';
  }

  if (hasAlerts) {
    classes.push('watchlist__row__alert--with_alerts');
  }

  if (account) {
    classes.push('watchlist__row__alert--active');
  } else {
    classes.push('watchlist__row__alert--inactive');
  }

  return { ...props, className: classes.join(' ') };
};

export enum ExchangeStatus {
  default,
  disabled,
  enabled,
}

export enum FavStep {
  default,
  color,
  favourite,
}

interface IWatchlistRowProps {
  account: boolean;
  item: ITicker | IWatchedTicker;
  active?: boolean;
  isTrending?: boolean;
  isFirst?: boolean;
  exchangeStatus?: ExchangeStatus;
  multiplier?: number;
  onAccountRequired: () => void;
  actionSwitchCurrent?: (payload: ITicker) => void;
  actionRemoveWatchlist?: (id: number, callback: () => void) => void;
  actionAddWatchlist?: (
    item: ITicker,
    tag?: enums.Tag,
    callback?: () => void
  ) => void;
  actionUpdateWatchlist?: (
    id: number,
    payload: UpdateWatchlistItemPayload
  ) => void;
}

const WatchlistRow = ({
  account,
  item,
  active,
  isTrending,
  isFirst,
  exchangeStatus = ExchangeStatus.default,
  multiplier,
  onAccountRequired,
  actionSwitchCurrent,
  actionRemoveWatchlist,
  actionAddWatchlist,
  actionUpdateWatchlist,
}: IWatchlistRowProps) => {
  const selectAlerts = useMemo(() => createGetAlerts(item), [item]);
  const alerts = useSelector(selectAlerts);

  const [isPriceAlert, setPriceAlert] = useState<boolean>(false);
  const [isColorPicker, setColorPicker] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [favStep, setFavStep] = useState<FavStep>(FavStep.default);
  const [tag, setTag] = useState<enums.Tag>();

  const ref = useRef<HTMLDivElement>(null);

  const { name, isDEX } = useTicker(item);
  const { price, side } = useTickerLivePrice(item);

  useEffect(() => {
    if (favStep === FavStep.color) {
      setColorPicker(true);
    }
    if (favStep === FavStep.favourite && ref.current) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionAddWatchlist && tag) {
          actionAddWatchlist(item, tag, actionCallback);
          setTag(undefined);
          setFavStep(FavStep.default);
          setColorPicker(false);
        }
      }, 150);
    }
  }, [actionAddWatchlist, favStep, item, tag]);

  const onPriceAlertClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (account) {
      setPriceAlert(!isPriceAlert);
    } else {
      onAccountRequired();
    }
  };

  const onSelect = useCallback(
    (e: any) => {
      if (actionSwitchCurrent) {
        e.stopPropagation();
        actionSwitchCurrent(item);
      }
    },
    [actionSwitchCurrent, item]
  );

  const actionCallback = () => {
    if (ref.current) {
      ref.current.classList.remove('watchlist__row--remove');
      setLoading(false);
    }
  };

  const onUnfav = useCallback(() => {
    if (ref.current && !loading) {
      setLoading(true);
      ref.current.classList.add('watchlist__row--remove');
      setTimeout(() => {
        if (actionRemoveWatchlist) {
          actionRemoveWatchlist(item.id, actionCallback);
        }
      }, 150);
    }
  }, [actionRemoveWatchlist, item.id, loading]);

  const onFav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (!loading) {
        setFavStep(FavStep.color);
      }
    },
    [loading]
  );

  const onUpdateTag = useCallback(
    (tag: enums.Tag) => {
      if (favStep === FavStep.color) {
        setTag(tag);
        setFavStep(FavStep.favourite);
      } else if (actionUpdateWatchlist) {
        actionUpdateWatchlist(item.id, { tag });
      }
    },
    [actionUpdateWatchlist, favStep, item]
  );

  let priceColor = 'text--grey-200';
  if (side === 'buy') {
    priceColor = 'text--green-600';
  } else if (side === 'sell') {
    priceColor = 'text--red-600';
  }

  const rect = ref.current?.getBoundingClientRect();

  const getExchange = () => {
    const exchange = capitalize(
      decorateExchangeName(item.exchange, item.chain)
    );
    if (exchangeStatus === ExchangeStatus.default) {
      return (
        <Text
          className='watchlist__row__exchange'
          style={{ title: exchange }}
          size={12}
        >
          {exchange}
        </Text>
      );
    }
    if (exchangeStatus === ExchangeStatus.disabled) {
      return (
        <div className='watchlist__row__exchange watchlist__row__exchange--disabled'>
          <Text size={12}>{exchange}</Text>
          <div className='watchlist__row__exchange__icon icon-alert-triangle' />
        </div>
      );
    }
    if (exchangeStatus === ExchangeStatus.enabled) {
      return (
        <div className='watchlist__row__exchange watchlist__row__exchange--enabled'>
          <Text size={12}>{exchange}</Text>
          <div className='watchlist__row__exchange__icon icon-check-circle' />
        </div>
      );
    }
  };

  return (
    <div
      ref={ref}
      className={`watchlist__row ${active ? 'watchlist__row--active' : ''}`}
      onClick={onSelect}
      onContextMenu={(e) => {
        if (actionUpdateWatchlist) {
          e.preventDefault();
          setColorPicker(!isColorPicker);
        }
      }}
    >
      {!isTrending && (
        <div className='watchlist__row__icon_column watchlist__row__icon_column--left'>
          {actionRemoveWatchlist && (
            <UnfavouriteStar
              tag={'tag' in item && item.tag ? item.tag : enums.Tag.Blue}
              onUnfav={onUnfav}
            />
          )}
          {actionAddWatchlist && (
            <div
              className='favourite icon-star'
              data-qa='addFavourite'
              onClick={onFav}
            />
          )}
        </div>
      )}

      <div
        className={`watchlist__row__content ${
          isTrending ? 'watchlist__row__content--no_alert' : ''
        }`}
      >
        <div className='watchlist__row__content__item'>
          {getExchange()}
          <NumberAnimation
            value={price.rawPrice}
            round={2}
            startSymbol={price.sign}
            textProps={{
              size: 14,
              className: priceColor,
            }}
          />
        </div>
        <div className='watchlist__row__content__item'>
          <div className='watchlist__row__text'>
            <Text className='text--grey-200' size={14} style={{ title: name }}>
              {name}
            </Text>
            {isTrending && (
              <div className='trend'>
                <Image src={flash} size='s' />
              </div>
            )}
            {multiplier && (
              <Text size={12} className='text--green-600'>
                x{multiplier} pts
              </Text>
            )}
          </div>
          <DailyPriceChange ticker={item} price={price} />
          {isPriceAlert && rect && (
            <PriceAlertCreate
              top={rect.top - 30}
              left={rect.left + 255}
              ticker={item}
              onClose={() => setPriceAlert(false)}
              alerts={alerts}
            />
          )}
          {isColorPicker && rect && (
            <ColorPicker
              top={rect.top - 80}
              left={rect.left - 5}
              onClose={() => {
                if (favStep === FavStep.color) {
                  onUpdateTag(enums.Tag.Blue);
                }
                setColorPicker(false);
              }}
              activeTag={'tag' in item ? item.tag : undefined}
              onSelect={onUpdateTag}
            />
          )}
        </div>
      </div>

      <div className='watchlist__row__icon_column watchlist__row__icon_column--right'>
        {isDEX && (
          <div
            onClick={onPriceAlertClick}
            {...getAlertProps(!!isFirst, alerts.length > 0, account)}
          >
            {isFirst && (
              <Image
                size='xxs'
                src={notice}
                className='watchlist__row__alert__notice'
              />
            )}
          </div>
        )}
        {isFirst && isDEX && (
          <ReactTooltip
            id='watchlist-first-item'
            type='light'
            place='bottom'
            multiline={true}
            className='watchlist__row__tooltip'
          />
        )}
      </div>
    </div>
  );
};

export default memo(WatchlistRow);
